import { ConfigProviderContext } from '@components/utils/config-provider'
import {
  createTheme,
  darken,
  inputBaseClasses,
  lighten,
  outlinedInputClasses,
  responsiveFontSizes,
} from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import MuiThemeProvider from '@mui/material/styles/ThemeProvider'
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
}

export const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

const initialConfig = {
  primary: '#1976d2',
  secondary: '#dc004e',
  error: '#f44336',
  warning: '#ff9800',
  info: '#2196f3',
  success: '#4caf50',
  backgroundPaper: '#fff',
  backgroundDefault: '#f5f5f5',
  backgroundPaperDark: '#15171e',
  backgroundDefaultDark: '#2c2d30',
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...rest
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem(storageKey) as Theme) || defaultTheme)
  const context = useContext(ConfigProviderContext)
  const config = context?.config || initialConfig

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme])

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme)
      setTheme(newTheme)
    },
  }

  const muiTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: config.primary,
      },
      secondary: {
        main: config.secondary,
      },
      info: {
        main: config.info,
      },
      error: {
        main: config.error,
      },
      background: {
        paper: config.backgroundPaper,
        default: config.backgroundDefault,
      },
    },
    shape: {
      borderRadius: 16,
    },
    typography: {
      h1: {
        fontSize: '3rem',
      },
      h2: {
        fontSize: '2.5rem',
      },
      h3: {
        fontSize: '2rem',
      },
      h4: {
        fontSize: '1.5rem',
      },
      h5: {
        fontSize: '1.25rem',
      },
      h6: {
        fontSize: '1rem',
      },
      body2: {
        marginTop: '0.3rem',
        marginBottom: '0.3rem',
      },
    },
    components: {
      MuiPaper: {
        defaultProps: {
          elevation: 1,
          variant: 'outlined',
        },
        styleOverrides: {
          root: (props) => ({
            border: `1px solid ${props.theme.palette.primary.light}`,
            background: lighten(props.theme.palette.background.paper, 0.25),
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: (props) => ({
            [`& .${inputBaseClasses.multiline}.${outlinedInputClasses.root}`]: {
              padding: '8px 14px',
              background: props.theme.palette.background.paper,
            },
          }),
        },
      },
    },
  })
  const muiDarkTheme = createTheme({
    ...muiTheme,
    palette: {
      mode: 'dark',
      primary: {
        main: config.primary,
      },
      secondary: {
        main: config.secondary,
      },
      info: {
        main: config.info,
      },
      error: {
        main: config.error,
      },
      background: {
        default: config?.backgroundDefaultDark || initialConfig.backgroundDefaultDark,
        paper: config?.backgroundPaperDark || initialConfig.backgroundPaperDark,
      },
    },

    components: {
      MuiPaper: {
        defaultProps: {
          elevation: 1,
          variant: 'outlined',
        },
        styleOverrides: {
          root: (props) => ({
            border: `1px solid ${props.theme.palette.primary.dark}`,
            background: darken(props.theme.palette.background.paper, 0.1),
          }),
        },
      },
    },
  })

  return (
    <ThemeProviderContext.Provider
      {...rest}
      value={value}
    >
      <MuiThemeProvider theme={responsiveFontSizes(theme === 'dark' ? muiDarkTheme : muiTheme)}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeProviderContext.Provider>
  )
}
